import type { IconName } from './components/Icon'

// Shared visual language for notifications (architecture §6.6) — the bell
// dropdown and the full notification center render the same glyphs, tones,
// relative times and date buckets, so they stay in lockstep.

type T = (key: string, params?: Record<string, string | number>) => string

export type Tone = 'tr' | 'ok' | 'warn' | 'bad' | 'vi'

export const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  tr: { bg: 'var(--accent-soft)', fg: 'var(--accent)' },
  ok: {
    bg: 'color-mix(in srgb, var(--status-delivered) 16%, transparent)',
    fg: 'var(--status-delivered)',
  },
  warn: {
    bg: 'color-mix(in srgb, var(--status-booked) 16%, transparent)',
    fg: 'var(--status-booked)',
  },
  bad: {
    bg: 'color-mix(in srgb, var(--status-delayed) 16%, transparent)',
    fg: 'var(--status-delayed)',
  },
  vi: {
    bg: 'color-mix(in srgb, var(--status-customs-hold) 16%, transparent)',
    fg: 'var(--status-customs-hold)',
  },
}

// Map an event key to a glyph + tone (mockup `.noti-ic` colours).
export function look(event: string): { icon: IconName; tone: Tone } {
  if (event.endsWith('.delivered') || event.endsWith('.paid') || event.endsWith('.confirmed'))
    return { icon: 'check-circle-2', tone: 'ok' }
  if (event.includes('exception') || event.includes('overdue') || event.includes('expired'))
    return { icon: 'alert-triangle', tone: 'bad' }
  if (event.startsWith('quote.')) return { icon: 'file-text', tone: 'vi' }
  if (event.startsWith('invoice.')) return { icon: 'receipt', tone: 'warn' }
  if (event === 'message.created' || event === 'inquiry.created')
    return { icon: 'messages-square', tone: 'tr' }
  if (event === 'document.exported') return { icon: 'download', tone: 'tr' }
  if (event === 'document.added') return { icon: 'file-text', tone: 'tr' }
  if (event.startsWith('shipment.')) return { icon: 'package', tone: 'tr' }
  return { icon: 'bell', tone: 'tr' }
}

export function timeAgo(iso: string, t: T): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return t('notif.ago.now')
  if (mins < 60) return t('notif.ago.minutes', { n: mins })
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return t('notif.ago.hours', { n: hrs })
  const days = Math.round(hrs / 24)
  return days === 1 ? t('notif.ago.yesterday') : t('notif.ago.days', { n: days })
}

// Coarse date bucket for the `.noti-sec` group headers.
export function bucket(iso: string, t: T): string {
  const days = (Date.now() - new Date(iso).getTime()) / 86_400_000
  if (days < 1) return t('notif.bucket.today')
  if (days < 2) return t('notif.bucket.yesterday')
  if (days < 7) return t('notif.bucket.this_week')
  return t('notif.bucket.earlier')
}
